class OahuCombineProduct extends HTMLElement {
  constructor() {
    super();
    this.handlePackChange = this.handlePackChange.bind(this);
    this.handleViewAll = this.handleViewAll.bind(this);
  }

  connectedCallback() {
    this.data = this.parseData();
    if (!this.data || !this.data.variants?.length) return;

    this.mediaRoot = this.querySelector('[data-ocp-media]');
    this.viewAllWrap = this.querySelector('[data-ocp-view-all-wrap]');
    this.infoRoot = this.querySelector('[data-ocp-info]');
    this.packInputs = Array.from(this.querySelectorAll('[data-ocp-pack-input]'));
    this.variantInput = this.querySelector('.product-variant-id');
    this.submitButton = this.querySelector('[type="submit"]');
    this.viewAllButton = this.querySelector('[data-ocp-view-all]');
    this.modalContent = document.querySelector(
      `#ProductModal-${this.data.sectionId} [data-ocp-modal-content]`
    );
    this.zoomIconTemplate = this.querySelector(`#OcpZoomIcon-${this.data.sectionId}`);
    this.zoomIconHtml = this.zoomIconTemplate ? this.zoomIconTemplate.innerHTML.trim() : '';

    this.packInputs.forEach((input) => {
      input.addEventListener('change', this.handlePackChange);
    });

    this.viewAllButton?.addEventListener('click', this.handleViewAll);

    const initialId = this.data.selectedVariantId || this.data.variants[0].id;
    this.selectVariant(initialId, { updateUrl: false, animate: false });
  }

  parseData() {
    const node = this.querySelector('[data-ocp-variant-data]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  getVariant(variantId) {
    return this.data.variants.find((variant) => String(variant.id) === String(variantId));
  }

  handlePackChange(event) {
    this.selectVariant(event.currentTarget.value, { updateUrl: true, animate: true, resetExpanded: true });
  }

  handleViewAll(event) {
    event.preventDefault();
    const extraGrid = this.mediaRoot?.querySelector('[data-ocp-media-extra]');
    if (extraGrid) {
      extraGrid.hidden = false;
    }
    this.viewAllWrap?.setAttribute('hidden', '');
  }

  selectVariant(variantId, options = {}) {
    const variant = this.getVariant(variantId);
    if (!variant) return;

    this.currentVariantId = variant.id;

    this.packInputs.forEach((input) => {
      input.checked = String(input.value) === String(variant.id);
    });

    if (this.variantInput) {
      this.variantInput.value = variant.id;
      this.variantInput.disabled = false;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.renderInfo(variant, options.animate);
    this.renderMedia(variant, options.animate, options.resetExpanded !== false);

    if (this.data.imageZoom === 'lightbox') {
      this.rebuildModal(variant);
    }

    if (this.submitButton) {
      this.submitButton.disabled = !variant.available;
      const labelText = this.submitButton.querySelector('.main-atc__label__text');
      if (labelText) {
        labelText.textContent = variant.available
          ? this.data.addToCartLabel
          : this.data.soldOutLabel;
      }
    }

    if (options.updateUrl && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.data.sectionId,
          variant
        }
      });
    }
  }

  renderInfo(variant, animate) {
    if (!this.infoRoot) return;

    if (animate) {
      this.infoRoot.classList.add('is-updating');
    }

    const titleVariant = this.infoRoot.querySelector('[data-ocp-title-variant]');
    const description = this.infoRoot.querySelector('[data-ocp-description]');
    const packLabel = this.infoRoot.querySelector('[data-ocp-pack-label]');
    const price = this.infoRoot.querySelector('[data-ocp-price]');
    const priceBesideText = this.infoRoot.querySelector('[data-ocp-price-beside-text]');
    const comparePrice = this.infoRoot.querySelector('[data-ocp-compare-price]');
    const keyFeatures = this.infoRoot.querySelector('[data-ocp-key-features]');

    if (titleVariant) titleVariant.textContent = variant.packLabel;
    if (description) description.innerHTML = variant.descriptionHtml || '';
    if (packLabel) packLabel.textContent = variant.packLabel;
    if (price) price.textContent = variant.priceFormatted;

    if (priceBesideText) {
      if (variant.priceBesideText) {
        priceBesideText.textContent = variant.priceBesideText;
        priceBesideText.hidden = false;
      } else {
        priceBesideText.textContent = '';
        priceBesideText.hidden = true;
      }
    }

    if (comparePrice) {
      if (variant.compareAtPriceFormatted && variant.compareAtPrice > variant.price) {
        comparePrice.textContent = variant.compareAtPriceFormatted;
        comparePrice.hidden = false;
      } else {
        comparePrice.hidden = true;
      }
    }

    if (keyFeatures) {
      keyFeatures.innerHTML = variant.keyFeaturesHtml || '';
      const details = keyFeatures.closest('details');
      if (details && this.data.keyFeaturesOpenByDefault) {
        details.open = true;
      }
    }

    window.requestAnimationFrame(() => {
      this.infoRoot.classList.remove('is-updating');
    });
  }

  renderZoomableImage(item, mediaIndex, options = {}) {
    const imageZoom = this.data.imageZoom || 'none';
    const hero = options.hero === true;
    const imageClass = hero
      ? 'oahu-combine-product__media-hero-img'
      : 'oahu-combine-product__media-img';
    const magnifyClass = `image-magnify-${imageZoom}`;

    const imgHtml = `
      <img
        src="${item.src}"
        alt="${this.escapeHtml(item.alt || '')}"
        loading="${hero ? 'eager' : 'lazy'}"
        width="${item.width || 1200}"
        height="${item.height || 1200}"
        class="${imageClass} ${magnifyClass}"
      >
    `;

    if (imageZoom !== 'lightbox') {
      return imgHtml;
    }

    const mediaId = `ocp-${this.data.sectionId}-${mediaIndex}`;
    const openLabel = this.escapeHtml(`Open media ${mediaIndex + 1}`);

    return `
      <div class="product-media-container media-fit-contain global-media-settings gradient oahu-combine-product__media-container">
        <modal-opener
          class="product__modal-opener product__modal-opener--image oahu-combine-product__modal-opener no-js-hidden"
          data-modal="#ProductModal-${this.data.sectionId}"
        >
          <span class="product__media-icon motion-reduce product__media-icon--${imageZoom}" aria-hidden="true">
            ${this.zoomIconHtml}
          </span>
          <div class="product__media media media--transparent">
            ${imgHtml}
          </div>
          <button
            class="product__media-toggle product__media-zoom-${imageZoom}"
            type="button"
            aria-haspopup="dialog"
            data-media-id="${mediaId}"
          >
            <span class="visually-hidden">${openLabel}</span>
          </button>
        </modal-opener>
      </div>
    `;
  }

  renderMedia(variant, animate, resetExpanded = true) {
    if (!this.mediaRoot) return;

    if (animate) {
      this.mediaRoot.classList.add('is-updating');
    }

    const media = variant.media?.length ? variant.media : [];
    const hero = media[0];
    const gridItems = media.slice(1, 5);
    const extraItems = media.slice(5);
    const badgesHtml =
      variant.badges?.length > 0
        ? `<div class="oahu-combine-product__media-badge">${variant.badges
            .map((badge) => `<span>${this.escapeHtml(badge)}</span>`)
            .join('')}</div>`
        : '';

    const heroHtml = hero ? this.renderZoomableImage(hero, 0, { hero: true }) : '';

    const gridHtml = gridItems
      .map(
        (item, index) => `
      <div class="oahu-combine-product__media-grid-item">
        ${this.renderZoomableImage(item, index + 1)}
      </div>
    `
      )
      .join('');

    const extraHtml = extraItems
      .map(
        (item, index) => `
      <div class="oahu-combine-product__media-grid-item">
        ${this.renderZoomableImage(item, index + 5)}
      </div>
    `
      )
      .join('');

    this.mediaRoot.innerHTML = `
      <div class="oahu-combine-product__media-stack">
        <div class="oahu-combine-product__media-card oahu-combine-product__media-card--single">
          ${heroHtml}
          ${badgesHtml}
        </div>
        ${
          gridItems.length
            ? `<div class="oahu-combine-product__media-grid">${gridHtml}</div>`
            : ''
        }
        ${
          extraItems.length
            ? `<div class="oahu-combine-product__media-grid oahu-combine-product__media-grid--extra" data-ocp-media-extra${
                resetExpanded ? ' hidden' : ''
              }>${extraHtml}</div>`
            : ''
        }
      </div>
    `;

    if (this.viewAllWrap) {
      if (variant.hasMoreMedia && resetExpanded) {
        this.viewAllWrap.removeAttribute('hidden');
      } else {
        this.viewAllWrap.setAttribute('hidden', '');
      }
    }

    window.requestAnimationFrame(() => {
      this.mediaRoot.classList.remove('is-updating');
    });
  }

  rebuildModal(variant) {
    if (!this.modalContent) return;

    const media = variant.media?.length ? variant.media : [];
    this.modalContent.innerHTML = media
      .map(
        (item, index) => `
        <img
          class="global-media-settings global-media-settings--no-shadow${index === 0 ? ' active' : ''}"
          src="${item.modalSrc || item.src}"
          alt="${this.escapeHtml(item.alt || '')}"
          loading="lazy"
          width="${item.width || 1946}"
          height="${item.height || 1946}"
          data-media-id="ocp-${this.data.sectionId}-${index}"
        >
      `
      )
      .join('');
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('oahu-combine-product')) {
  customElements.define('oahu-combine-product', OahuCombineProduct);
}
